import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Gallery", path: "/gallery" },
    { name: "Resources", path: "/guides/bathroom-renovation-cost" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="nav-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/een5v61h_1.png" 
              alt="J.N. Nickel Contracting Inc."
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`font-['Barlow_Condensed'] font-semibold text-sm uppercase tracking-wider transition-colors ${
                  isActive(link.path)
                    ? "text-[#d97706]"
                    : "text-[#0f172a] hover:text-[#d97706]"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <a 
              href="tel:+17783455884" 
              className="hidden md:flex items-center gap-2 text-[#0f172a] hover:text-[#d97706] transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">778-345-5884</span>
            </a>
            
            <Link to="/contact" className="hidden sm:block">
              <Button 
                className="bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider btn-glow"
                data-testid="nav-cta-btn"
              >
                Get Quote
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-[#0f172a]"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200 animate-fade-in">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`font-['Barlow_Condensed'] font-semibold text-lg uppercase tracking-wider py-3 px-4 transition-colors ${
                    isActive(link.path)
                      ? "text-[#d97706] bg-amber-50"
                      : "text-[#0f172a] hover:text-[#d97706] hover:bg-slate-50"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 px-4 flex flex-col gap-3">
                <a 
                  href="tel:+17783455884" 
                  className="flex items-center gap-2 text-[#0f172a] font-semibold"
                >
                  <Phone className="w-4 h-4" />
                  778-345-5884
                </a>
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider">
                    Get Free Quote
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
